import React, { useEffect, useState } from "react";
import { HelpCircle, Sparkles, LayoutDashboard, Wrench, Users, ArrowRight, ArrowLeft } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { Modal } from "./Modal";

const KEY = "mc_tutorial_v1";
const ICONS = [Sparkles, LayoutDashboard, Wrench, Users];

const TUT = {
  pt: { title: "Como funciona o MY CHRISTMAS", start: "Começar", back: "Voltar", next: "Próximo", done: "Entendi!", help: "Como funciona",
    steps: [
      ["✨ Crie o seu Natal", "Responda algumas perguntas (país, convidados, orçamento e estilo) e o MY CHRISTMAS monta um plano personalizado para você."],
      ["🎄 Seu painel", "Veja quantos dias faltam, o que fazer agora, seu progresso e o orçamento reservado para cada parte da celebração."],
      ["🧰 Ferramentas", "Organize decoração, ceia, receitas, mesa, presentes e Amigo Oculto — tudo em um só lugar."],
      ["👨‍👩‍👧 Família e Momento", "Convide sua família para ajudar, divida tarefas e viva o Momento de Natal. Use o botão “Voltar ao início” para retornar ao painel a qualquer momento."],
    ] },
  "pt-PT": { title: "Como funciona o MY CHRISTMAS", start: "Começar", back: "Voltar", next: "Seguinte", done: "Percebi!", help: "Como funciona",
    steps: [
      ["✨ Crie o seu Natal", "Responda a algumas perguntas (país, convidados, orçamento e estilo) e o MY CHRISTMAS monta um plano personalizado para si."],
      ["🎄 O seu painel", "Veja quantos dias faltam, o que fazer agora, o seu progresso e o orçamento reservado para cada parte da celebração."],
      ["🧰 Ferramentas", "Organize a decoração, a ceia, as receitas, a mesa, os presentes e o Amigo Secreto — tudo num só lugar."],
      ["👨‍👩‍👧 Família e Momento", "Convide a sua família para ajudar, divida tarefas e viva o Momento de Natal. Use o botão “Voltar ao início” para regressar ao painel a qualquer momento."],
    ] },
  en: { title: "How MY CHRISTMAS works", start: "Start", back: "Back", next: "Next", done: "Got it!", help: "How it works",
    steps: [
      ["✨ Create your Christmas", "Answer a few questions (country, guests, budget and style) and MY CHRISTMAS builds a personalized plan for you."],
      ["🎄 Your dashboard", "See the countdown, what to do now, your progress and the budget reserved for each part of the celebration."],
      ["🧰 Tools", "Organize decoration, dinner, recipes, table, gifts and Secret Santa — all in one place."],
      ["👨‍👩‍👧 Family & Moment", "Invite your family to help, split tasks and live the Christmas Moment. Use the “Back to home” button to return to the dashboard anytime."],
    ] },
  es: { title: "Cómo funciona MY CHRISTMAS", start: "Empezar", back: "Volver", next: "Siguiente", done: "¡Entendido!", help: "Cómo funciona",
    steps: [
      ["✨ Crea tu Navidad", "Responde algunas preguntas (país, invitados, presupuesto y estilo) y MY CHRISTMAS crea un plan personalizado para ti."],
      ["🎄 Tu panel", "Mira la cuenta regresiva, qué hacer ahora, tu progreso y el presupuesto reservado para cada parte."],
      ["🧰 Herramientas", "Organiza decoración, cena, recetas, mesa, regalos y amigo invisible, todo en un lugar."],
      ["👨‍👩‍👧 Familia y Momento", "Invita a tu familia a ayudar, divide tareas y vive el Momento de Navidad. Usa “Volver al inicio” para regresar al panel."],
    ] },
  fr: { title: "Comment fonctionne MY CHRISTMAS", start: "Commencer", back: "Retour", next: "Suivant", done: "Compris !", help: "Comment ça marche",
    steps: [
      ["✨ Créez votre Noël", "Répondez à quelques questions (pays, invités, budget et style) et MY CHRISTMAS crée un plan personnalisé."],
      ["🎄 Votre tableau de bord", "Voyez le compte à rebours, quoi faire maintenant, votre progression et le budget réservé à chaque partie."],
      ["🧰 Outils", "Organisez décoration, repas, recettes, table, cadeaux et Père Noël secret, au même endroit."],
      ["👨‍👩‍👧 Famille & Moment", "Invitez votre famille, répartissez les tâches et vivez le Moment de Noël. Utilisez « Retour à l'accueil » pour revenir au tableau de bord."],
    ] },
  it: { title: "Come funziona MY CHRISTMAS", start: "Inizia", back: "Indietro", next: "Avanti", done: "Capito!", help: "Come funziona",
    steps: [
      ["✨ Crea il tuo Natale", "Rispondi ad alcune domande (paese, ospiti, budget e stile) e MY CHRISTMAS crea un piano personalizzato."],
      ["🎄 La tua dashboard", "Guarda il conto alla rovescia, cosa fare ora, i progressi e il budget riservato a ogni parte."],
      ["🧰 Strumenti", "Organizza decorazione, cena, ricette, tavola, regali e amico segreto, tutto in un posto."],
      ["👨‍👩‍👧 Famiglia e Momento", "Invita la famiglia, dividi le attività e vivi il Momento di Natale. Usa “Torna all'inizio” per tornare alla dashboard."],
    ] },
};

export default function TutorialModal() {
  const { lang } = useI18n();
  const T = TUT[lang] || TUT.pt;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => { if (localStorage.getItem(KEY) !== "done") { setOpen(true); setStep(0); } }, []);
  const close = () => { setOpen(false); localStorage.setItem(KEY, "done"); };
  const reopen = () => { setStep(0); setOpen(true); };

  const total = T.steps.length;
  const [ti, de] = T.steps[step];
  const Icon = ICONS[step] || Sparkles;
  const last = step === total - 1;

  return (
    <>
      <button onClick={reopen} data-testid="tutorial-help-button" aria-label={T.help} title={T.help}
        className="fixed bottom-36 right-4 z-40 flex h-11 w-11 items-center justify-center rounded-full border border-xmas-gold/30 bg-xmas-navycard/80 text-xmas-gold shadow-lg backdrop-blur transition hover:border-xmas-gold lg:bottom-20">
        <HelpCircle className="h-5 w-5" />
      </button>

      <Modal open={open} onClose={close} title={T.title} testid="tutorial-modal" centerTitle>
        <div className="text-center" data-testid={`tutorial-step-${step}`}>
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-xmas-gold/15"><Icon className="h-7 w-7 text-xmas-gold" /></div>
          <h3 className="font-display text-xl text-xmas-snow">{ti}</h3>
          <p className="mx-auto mt-3 max-w-sm text-sm text-xmas-slate">{de}</p>
          <div className="mt-6 flex items-center justify-center gap-1.5">
            {T.steps.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-xmas-gold" : "w-1.5 bg-xmas-slate/40"}`} />)}
          </div>
          <div className="mt-6 flex items-center justify-between">
            {step > 0 ? (
              <button onClick={() => setStep(step - 1)} data-testid="tutorial-back" className="inline-flex items-center gap-1 text-sm text-xmas-slate hover:text-xmas-snow"><ArrowLeft className="h-4 w-4" /> {T.back}</button>
            ) : <span />}
            {last ? (
              <button onClick={close} data-testid="tutorial-done" className="btn-gold rounded-full px-6 py-2.5 text-sm font-bold uppercase tracking-wider">{T.done}</button>
            ) : (
              <button onClick={() => setStep(step + 1)} data-testid="tutorial-next" className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold uppercase tracking-wider">{T.next} <ArrowRight className="h-4 w-4" /></button>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
